import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { Consts } from '../configs/consts'
import type { ForgotPasswordParams, LoginParams, ResetPasswordParams } from '../services/api/api.types'
import type { User } from '../types/payload'

const oneLowerRx = /(?=.*?[a-z])/
const oneUpperRx = /(?=.*?[A-Z])/
const oneNumberRx = /(?=.*?[0-9])/
const oneSpecialRx = /(?=.*?[#?!@$%^&*-])/
const min8Rx = /.{8,}/

//
// SCHEMAS
//
export const loginSchema = z.object({
  email: z.string().email({ message: 'Invalid email address.' }),
  password: z.string().min(8, 'Must be at least 8 characters long.'),
})

export const resetPasswordSchema = z
  .object({
    token: z.string(),
    password: z.string().min(8).regex(oneUpperRx).regex(oneLowerRx).regex(oneNumberRx).regex(oneSpecialRx),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  })

export const forgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Invalid email address.' }),
})

export const userSchema = z.object({
  firstName: z.string().min(1, "First name can't be blank."),
  lastName: z.string().min(1, "Last name can't be blank."),
  email: z.string().min(1, "Email can't be blank.").email({ message: 'Invalid email address.' }),
  role: z.enum(['admin', 'supervisor', 'member'] as const satisfies User['role'][]),
  accountStatus: z.enum(['enabled', 'disabled'] as const satisfies User['accountStatus'][]),
  visibilityStatus: z.enum(['internal', 'public', 'archived'] as const satisfies User['visibilityStatus'][]),
  internalNotes: z.string(),
  memberNotes: z.string(),
})

//
// DEFAULTS
//
export function castLoginToForm(data?: Partial<LoginParams>): z.infer<typeof loginSchema> {
  return {
    email: data?.email ?? '',
    password: Consts.isProd ? '' : (data?.password ?? ''),
  }
}

export function castResetPasswordToForm(data?: Partial<ResetPasswordParams>): z.infer<typeof resetPasswordSchema> {
  return {
    token: data?.token ?? '',
    password: '',
    confirmPassword: '',
  }
}

export function castForgotPasswordToForm(data?: Partial<ForgotPasswordParams>): z.infer<typeof forgotPasswordSchema> {
  return {
    email: data?.email ?? '',
  }
}

export function castUserToForm(data?: Partial<User>): z.infer<typeof userSchema> {
  return {
    firstName: data?.firstName ?? '',
    lastName: data?.lastName ?? '',
    email: data?.email ?? '',
    accountStatus: data?.accountStatus ?? 'enabled',
    visibilityStatus: data?.visibilityStatus ?? 'public',
    internalNotes: data?.internalNotes ?? '',
    memberNotes: data?.memberNotes ?? '',
    role: data?.role ?? 'member',
  }
}

//
// HOOKS
//
export function useLoginForm(data?: Partial<LoginParams>) {
  const form = useForm<z.infer<typeof loginSchema>>({
    mode: 'controlled',
    validate: zodResolver(loginSchema),
    initialValues: castLoginToForm(data),
  })

  return { form, schema: loginSchema }
}

export function useResetPasswordForm(data?: Partial<ResetPasswordParams>) {
  const form = useForm<z.infer<typeof resetPasswordSchema>>({
    mode: 'controlled',
    validate: zodResolver(resetPasswordSchema),
    initialValues: castResetPasswordToForm(data),
  })

  return {
    form,
    schema: resetPasswordSchema,
    passwordRequirements: { oneLowerRx, oneUpperRx, oneNumberRx, oneSpecialRx, min8Rx },
  }
}

export function useForgotPasswordForm(data?: Partial<ForgotPasswordParams>) {
  const form = useForm<z.infer<typeof forgotPasswordSchema>>({
    mode: 'controlled',
    validate: zodResolver(forgotPasswordSchema),
    initialValues: castForgotPasswordToForm(data),
  })

  return { form, schema: forgotPasswordSchema }
}

export function useUserForm(data?: Partial<User>) {
  const form = useForm<z.infer<typeof userSchema>>({
    mode: 'controlled',
    validate: zodResolver(userSchema),
    validateInputOnChange: true,
    initialValues: castUserToForm(data),
  })

  return { form, schema: userSchema }
}

//
// TYPECHECKS
//
const _userKeysTypecheck: keyof User = '' as keyof z.infer<typeof userSchema>
const _loginKeysTypecheck: keyof LoginParams = '' as keyof z.infer<typeof loginSchema>
const _resetPasswordKeysTypecheck: keyof ResetPasswordParams = '' as Exclude<
  keyof z.infer<typeof resetPasswordSchema>,
  'confirmPassword'
>
const _forgotPasswordKeysTypecheck: keyof ForgotPasswordParams = '' as keyof z.infer<typeof forgotPasswordSchema>

//
// EXPORTS
//
export type UserSchema = typeof userSchema
export type LoginSchema = typeof loginSchema
export type ResetPasswordSchema = typeof resetPasswordSchema
export type ForgotPasswordSchema = typeof forgotPasswordSchema
